/**
 * Modal d'analyse intelligente : scanne toutes les matières × classes selon
 * un mode (sans aucune note / partielle), affiche le résultat avec checkboxes,
 * et permet d'exécuter une action en masse (report ou note collective) sur les
 * matières sélectionnées.
 *
 * 4 cas d'usage déclenchés depuis NotesList :
 *   1. examen_sans_note   → reporter D vers E (TP, etc.)
 *   2. examen_partielle   → note collective sur cases vides examens
 *   3. devoir_sans_note   → reporter E vers D
 *   4. devoir_partielle   → note collective sur cases vides devoirs
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  X, Loader, AlertTriangle, CheckCircle2, Filter, RefreshCw, Search,
  ArrowRightLeft, Award, Zap, Users,
} from 'lucide-react';
import apiClient from '../../config/api.config';

type ScenarioId = 'examen_sans_note' | 'examen_partielle' | 'devoir_sans_note' | 'devoir_partielle';

interface ItemAnalyse {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  semestre_matiere: number;
  option: string;
  ue_code: string;
  classe: string;
  nb_etudiants: number;
  nb_avec_note: number;
  nb_sans_note: number;
  pct_couvert: number;
  statut: 'sans_aucune_note' | 'partielle' | 'complete';
}

interface Props {
  open: boolean;
  onClose: () => void;
  scenario: ScenarioId | null;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  onSuccess?: () => void;
}

const SCENARIO_META: Record<ScenarioId, {
  titre: string;
  description: string;
  endpoint_analyse: string;
  mode: 'sans_aucune_note' | 'partielle';
  type: 'examen' | 'devoir';
  actionLabel: string;
  actionType: 'report_d_vers_e' | 'note_coll_examen' | 'report_e_vers_d' | 'note_coll_devoir';
  color: 'blue' | 'cyan' | 'emerald' | 'purple';
}> = {
  examen_sans_note: {
    titre: 'Matières d\'examen sans aucune note',
    description: 'Sélectionne les matières → reporter la moyenne des devoirs vers la note d\'examen (cas TP, examen non fait, etc.)',
    endpoint_analyse: '/notes-examens/analyser-globale/',
    mode: 'sans_aucune_note',
    type: 'examen',
    actionLabel: 'Reporter Devoirs → Examen',
    actionType: 'report_d_vers_e',
    color: 'blue',
  },
  examen_partielle: {
    titre: 'Matières d\'examen avec cases vides',
    description: 'Sélectionne les matières → attribue une note collective uniquement aux étudiants sans note d\'examen.',
    endpoint_analyse: '/notes-examens/analyser-globale/',
    mode: 'partielle',
    type: 'examen',
    actionLabel: 'Note collective (cases vides)',
    actionType: 'note_coll_examen',
    color: 'cyan',
  },
  devoir_sans_note: {
    titre: 'Matières de devoirs sans aucune note',
    description: 'Sélectionne les matières → reporter la note d\'examen vers les devoirs (D1+D2).',
    endpoint_analyse: '/notes-devoirs/analyser-globale/',
    mode: 'sans_aucune_note',
    type: 'devoir',
    actionLabel: 'Reporter Examen → Devoirs',
    actionType: 'report_e_vers_d',
    color: 'emerald',
  },
  devoir_partielle: {
    titre: 'Matières de devoirs avec cases vides',
    description: 'Sélectionne les matières → attribue une note collective sur les cases D1/D2 vides uniquement.',
    endpoint_analyse: '/notes-devoirs/analyser-globale/',
    mode: 'partielle',
    type: 'devoir',
    actionLabel: 'Note collective (cases vides)',
    actionType: 'note_coll_devoir',
    color: 'purple',
  },
};

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-600 hover:bg-blue-700', tile: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300' },
  cyan: { bg: 'bg-cyan-600 hover:bg-cyan-700', tile: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800', text: 'text-cyan-700 dark:text-cyan-300' },
  emerald: { bg: 'bg-emerald-600 hover:bg-emerald-700', tile: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
  purple: { bg: 'bg-purple-600 hover:bg-purple-700', tile: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300' },
};

const AnalyseIntelligenteModal: React.FC<Props> = ({ open, onClose, scenario, anneeAcademique, semestre, onSuccess }) => {
  const [niveau, setNiveau] = useState('');
  const [pole, setPole] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<ItemAnalyse[]>([]);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [analyseError, setAnalyseError] = useState<string | null>(null);

  // Champs spécifiques à l'action
  const [valeurCollective, setValeurCollective] = useState('');
  const [reportSourceDevoir, setReportSourceDevoir] = useState<'note1' | 'note2' | 'moyenne'>('moyenne');
  const [reportSession, setReportSession] = useState<'normale' | 'rattrapage'>('normale');
  const [executing, setExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState<string[]>([]);
  const [executionResume, setExecutionResume] = useState<{ ok: number; skip: number; err: number } | null>(null);

  const meta = scenario ? SCENARIO_META[scenario] : null;
  const colors = meta ? COLOR_CLASSES[meta.color] : COLOR_CLASSES.blue;

  // Charger l'analyse à l'ouverture / changement de filtres
  useEffect(() => {
    if (!open || !meta) return;
    setLoadingAnalyse(true);
    setAnalyseError(null);
    setItems([]);
    setSelected(new Set());
    apiClient.get(meta.endpoint_analyse, {
      params: {
        mode: meta.mode,
        annee_academique: anneeAcademique,
        semestre,
        ...(niveau ? { niveau } : {}),
        ...(pole ? { pole } : {}),
      },
    })
      .then(res => setItems(res.data.items || []))
      .catch(e => setAnalyseError(e?.response?.data?.error || e?.message || 'Erreur lors de l\'analyse'))
      .finally(() => setLoadingAnalyse(false));
  }, [open, scenario, anneeAcademique, semestre, niveau, pole, meta]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) {
      setItems([]);
      setSelected(new Set());
      setExecutionLog([]);
      setExecutionResume(null);
      setValeurCollective('');
      setSearch('');
      setNiveau('');
      setPole('');
    }
  }, [open]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(i =>
      i.matiere_nom.toLowerCase().includes(q) ||
      i.matiere_code.toLowerCase().includes(q) ||
      i.classe.toLowerCase().includes(q) ||
      (i.option || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map(i => i.matiere_id * 100000 + 0)));  // dummy unique key
  };

  const itemKey = (i: ItemAnalyse) => i.matiere_id * 100000 + (i.classe.charCodeAt(0) || 0) + i.classe.length;
  const toggleOne = (i: ItemAnalyse) => {
    const k = itemKey(i);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const isSelected = (i: ItemAnalyse) => selected.has(itemKey(i));

  // Sélection rapide
  const selectAll = () => setSelected(new Set(filteredItems.map(itemKey)));
  const clearSelection = () => setSelected(new Set());

  const handleExecute = async () => {
    if (!meta) return;
    const selectedItems = filteredItems.filter(i => isSelected(i));
    if (selectedItems.length === 0) {
      alert('Sélectionne au moins une matière dans la liste.');
      return;
    }

    // Validation valeur collective si applicable
    let valeurNum: number | null = null;
    if (meta.actionType === 'note_coll_examen' || meta.actionType === 'note_coll_devoir') {
      valeurNum = parseFloat(valeurCollective);
      if (isNaN(valeurNum) || valeurNum < 0 || valeurNum > 20) {
        alert('Saisis une note valide (0-20) pour la note collective.');
        return;
      }
    }

    if (!window.confirm(
      `Action : ${meta.actionLabel}\n\n` +
      `${selectedItems.length} matière(s) × classe(s) sélectionnée(s).\n` +
      `Toutes les modifications sont tracées (batch_id) et annulables.\n\n` +
      `Continuer ?`
    )) return;

    setExecuting(true);
    setExecutionLog([]);
    setExecutionResume({ ok: 0, skip: 0, err: 0 });
    let okCount = 0, skipCount = 0, errCount = 0;

    for (const item of selectedItems) {
      try {
        // Récupérer les étudiants de la classe (nécessaire pour reports)
        let etudiants_ids: number[] = [];
        if (meta.actionType === 'report_d_vers_e' || meta.actionType === 'report_e_vers_d') {
          const r = await apiClient.get('/etudiants/', { params: { classe: item.classe, actif: true, page_size: 500 } });
          const list = Array.isArray(r.data) ? r.data : (r.data?.results || []);
          etudiants_ids = list.map((e: any) => e.id);
          if (etudiants_ids.length === 0) {
            errCount++;
            setExecutionLog(l => [...l, `❌ ${item.matiere_code} ${item.classe} — aucun étudiant trouvé`]);
            continue;
          }
        }

        let response: any;
        switch (meta.actionType) {
          case 'report_d_vers_e':
            response = await apiClient.post('/notes-examens/reporter-devoir-vers-examen-trace/', {
              matiere_id: item.matiere_id,
              annee_academique: anneeAcademique,
              semestre,
              etudiants_ids,
              source: reportSourceDevoir,
              mode: 'manquants_seulement',
              commentaire: `Analyse intelligente — ${item.classe} → SO`,
            });
            break;
          case 'report_e_vers_d':
            response = await apiClient.post('/notes-devoirs/reporter-examen-vers-devoirs-trace/', {
              matiere_id: item.matiere_id,
              annee_academique: anneeAcademique,
              semestre,
              etudiants_ids,
              mode: 'manquants_seulement',
              commentaire: `Analyse intelligente — ${item.classe} → D1+D2`,
            });
            break;
          case 'note_coll_examen':
            response = await apiClient.post('/notes-examens/note-collective/', {
              matiere_id: item.matiere_id,
              classes: [item.classe],
              annee_academique: anneeAcademique,
              semestre,
              valeur: valeurNum,
              ecraser_existants: false,
              commentaire: `Analyse intelligente — note collective examen vides — ${item.classe}`,
            });
            break;
          case 'note_coll_devoir':
            response = await apiClient.post('/notes-devoirs/note_collective/', {
              matiere_id: item.matiere_id,
              classes: [item.classe],
              annee_academique: anneeAcademique,
              semestre,
              champ: 'les_deux',
              valeur: valeurNum,
              ecraser_existants: false,
              commentaire: `Analyse intelligente — note collective devoirs vides — ${item.classe}`,
            });
            break;
        }
        const d = response.data;
        const total = (d.nb_crees ?? d.crees ?? 0) + (d.nb_modifies ?? d.modifies ?? 0);
        if (total > 0) {
          okCount++;
          setExecutionLog(l => [...l, `✅ ${item.matiere_code} ${item.classe} — ${total} note(s) traitée(s)`]);
        } else {
          skipCount++;
          setExecutionLog(l => [...l, `⏭ ${item.matiere_code} ${item.classe} — 0 note (déjà saisies ou conditions non remplies)`]);
        }
      } catch (e: any) {
        errCount++;
        const errMsg = e?.response?.data?.error || e?.message || 'Erreur inconnue';
        setExecutionLog(l => [...l, `❌ ${item.matiere_code} ${item.classe} — ${errMsg}`]);
      }
      setExecutionResume({ ok: okCount, skip: skipCount, err: errCount });
    }

    setExecuting(false);
    if (onSuccess) onSuccess();
  };

  if (!open || !meta) return null;

  const allSelected = filteredItems.length > 0 && selected.size === filteredItems.length;
  const someSelected = selected.size > 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`px-5 py-3 border-b dark:border-gray-700 ${colors.tile} flex items-center justify-between`}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Zap size={20} className={colors.text} />
              {meta.titre}
            </h3>
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">{meta.description}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* Filtres + paramètres action */}
        <div className="px-5 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-2">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Filter size={14} className="text-gray-500" />
            <select
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            >
              <option value="">Tous niveaux</option>
              {['L1','L2','L3','M1','M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <input
              type="text"
              value={pole}
              onChange={(e) => setPole(e.target.value)}
              placeholder="Pôle (optionnel)"
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
            <div className="relative flex-1 min-w-[180px]">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer dans les résultats…"
                className="w-full pl-7 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              />
            </div>
            <span className="text-gray-600 dark:text-gray-400 ml-auto">
              Année <strong>{anneeAcademique}</strong> · Sem <strong>{semestre}</strong>
            </span>
          </div>

          {/* Paramètres action */}
          {meta.actionType === 'report_d_vers_e' && (
            <div className="flex items-center gap-3 text-xs">
              <span className="font-medium text-gray-700 dark:text-gray-300">Source devoir → examen :</span>
              <select
                value={reportSourceDevoir}
                onChange={(e) => setReportSourceDevoir(e.target.value as any)}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              >
                <option value="moyenne">Moyenne (D1+D2)/2 — recommandé</option>
                <option value="note1">D1 uniquement</option>
                <option value="note2">D2 uniquement</option>
              </select>
              <span className="text-gray-500">Mode "cases vides uniquement" automatique</span>
            </div>
          )}

          {(meta.actionType === 'note_coll_examen' || meta.actionType === 'note_coll_devoir') && (
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-gray-700 dark:text-gray-300">Note à attribuer (cases vides) :</span>
              <input
                type="number" min="0" max="20" step="0.25"
                value={valeurCollective}
                onChange={(e) => setValeurCollective(e.target.value)}
                placeholder="ex: 10"
                className="w-20 px-2 py-1 text-sm font-bold text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              />
              <span className="text-gray-500">/ 20 — uniquement les étudiants sans note</span>
            </div>
          )}
        </div>

        {/* Toolbar sélection */}
        <div className="px-5 py-2 border-b dark:border-gray-700 flex items-center gap-2 text-xs">
          <button onClick={toggleAll} disabled={filteredItems.length === 0}
            className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 disabled:opacity-50">
            {allSelected ? 'Tout décocher' : 'Tout cocher'}
          </button>
          <button onClick={clearSelection} disabled={!someSelected}
            className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 disabled:opacity-50">
            Effacer sélection
          </button>
          <span className="text-gray-600 dark:text-gray-400 ml-2">
            <strong>{selected.size}</strong> sélectionné(s) sur {filteredItems.length} affichées · {items.length} au total
          </span>
        </div>

        {/* Tableau */}
        <div className="flex-1 overflow-auto">
          {loadingAnalyse ? (
            <div className="text-center py-12">
              <Loader className="animate-spin mx-auto text-blue-600" size={32} />
              <p className="mt-2 text-sm text-gray-500">Analyse en cours… ({items.length} déjà chargés)</p>
            </div>
          ) : analyseError ? (
            <div className="p-4 m-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 rounded">
              ❌ {analyseError}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              Aucune matière correspondante. {items.length === 0 && '(Aucun cas détecté avec ces filtres.)'}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 z-10">
                <tr className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  <th className="px-2 py-2 text-center w-8"></th>
                  <th className="px-2 py-2 text-left">Code</th>
                  <th className="px-2 py-2 text-left">Matière</th>
                  <th className="px-2 py-2 text-left">Niveau</th>
                  <th className="px-2 py-2 text-left">Option</th>
                  <th className="px-2 py-2 text-center">Classe</th>
                  <th className="px-2 py-2 text-center">Étudiants</th>
                  <th className="px-2 py-2 text-center">Avec note</th>
                  <th className="px-2 py-2 text-center">Sans note</th>
                  <th className="px-2 py-2 text-center">% couvert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredItems.map(i => {
                  const sel = isSelected(i);
                  return (
                    <tr
                      key={`${i.matiere_id}-${i.classe}`}
                      onClick={() => toggleOne(i)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                        sel ? `${colors.tile} ring-1 ring-inset ring-${meta.color}-300` : ''
                      }`}
                    >
                      <td className="px-2 py-1 text-center">
                        <input type="checkbox" checked={sel} onChange={() => toggleOne(i)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td className="px-2 py-1 font-mono text-[11px]">{i.matiere_code}</td>
                      <td className="px-2 py-1 max-w-[260px] truncate" title={i.matiere_nom}>{i.matiere_nom}</td>
                      <td className="px-2 py-1">{i.niveau}</td>
                      <td className="px-2 py-1 max-w-[180px] truncate" title={i.option}>{i.option || '—'}</td>
                      <td className="px-2 py-1 text-center">
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-[10px] font-semibold">
                          {i.classe}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className="flex items-center justify-center gap-1 text-gray-700 dark:text-gray-300">
                          <Users size={10} /> {i.nb_etudiants}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center font-semibold text-green-700 dark:text-green-400">
                        {i.nb_avec_note}
                      </td>
                      <td className="px-2 py-1 text-center font-semibold text-red-700 dark:text-red-400">
                        {i.nb_sans_note}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <div className="inline-flex items-center gap-1">
                          <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                            <div
                              className={`h-full ${i.pct_couvert >= 80 ? 'bg-green-500' : i.pct_couvert >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${i.pct_couvert}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-gray-600 dark:text-gray-400 w-7 text-right">{i.pct_couvert}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Log d'exécution */}
        {executionLog.length > 0 && (
          <div className="px-5 py-2 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 max-h-40 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Journal d'exécution</div>
            {executionResume && (
              <div className="text-xs mb-2 flex gap-3">
                <span className="text-green-700 dark:text-green-400">✅ {executionResume.ok} OK</span>
                <span className="text-amber-700 dark:text-amber-400">⏭ {executionResume.skip} sans effet</span>
                <span className="text-red-700 dark:text-red-400">❌ {executionResume.err} erreur(s)</span>
              </div>
            )}
            <div className="space-y-0.5 font-mono text-[10px]">
              {executionLog.slice(-30).map((line, i) => <div key={i} className="text-gray-700 dark:text-gray-300">{line}</div>)}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t dark:border-gray-700 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-300">
            Fermer
          </button>
          <button
            onClick={handleExecute}
            disabled={!someSelected || executing}
            className={`flex items-center gap-2 px-4 py-1.5 ${colors.bg} text-white rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {executing ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
            {executing ? 'Exécution…' : `${meta.actionLabel} (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyseIntelligenteModal;
